// Deterministic source chunker (§pass1-prepare). Splits normalized (LF) source
// text into contiguous, non-overlapping chunks under a char budget. Blank lines
// stay with the preceding content, so paragraph boundaries are preserved when
// they occur near a cut; line-oriented sources without blank separators fall
// back to line boundaries. It does NOT interpret episode/scene structure — that
// is pass2-normalize's job. Lines carry stable 0-based indices; a chunk's core
// range is the half-open [core_line_start, core_line_end). A single overlong line
// may exceed the budget because cuts never split a Unicode line.

export type Chunk = {
  index: number;
  core_line_start: number;
  core_line_end: number;
  text: string;
};

const DEFAULT_MAX_CHARS = 500;

export function chunkSource(text: string, opts: { maxChars?: number } = {}): Chunk[] {
  const maxChars = opts.maxChars ?? DEFAULT_MAX_CHARS;
  const lines = text.split("\n");
  const chunks: Chunk[] = [];
  let start = 0;
  let acc = 0;

  const push = (end: number): boolean => {
    const chunkText = lines.slice(start, end).join("\n");
    if (chunkText.trim() === "") return false;
    chunks.push({
      index: chunks.length,
      core_line_start: start,
      core_line_end: end,
      text: chunkText,
    });
    start = end;
    acc = 0;
    return true;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const separatorChars = i === start ? 0 : 1;
    const projected = acc + separatorChars + line.length;

    // Keep blank separators with the preceding content. Before the next
    // non-blank line crosses the budget, cut at its line boundary. If the source
    // has no blank lines this still bounds every chunk to roughly maxChars.
    if (line.trim() !== "" && i > start && projected > maxChars && push(i)) {
      acc = line.length;
    } else {
      acc = projected;
    }
  }
  push(lines.length);
  return chunks;
}

// ponytail: no context-only windows yet — normalize sees whole chunk text.
// Add a prefix/suffix context band if cross-chunk pronoun resolution needs it.
